import { useState, useEffect, useCallback, useMemo, useContext } from 'react';
import {
  getOrgPrintDefaults,
  updateTemplatePrintOverrides,
  resetTemplatePrintOverrides,
  PRINT_DEFAULTS_STATUS,
  EMPTY_FLAT_TEMPLATE_OVERRIDES,
  fetchOrgPrintCatalogMap,
  getOrganizationIdForPrintCatalog,
} from '../../../../utils/services/printDefaultsAPI';
import { useDocumentContext, COMPLEX_DOCUMENT_CONTEXT_KEYS } from '../../../../context/complexDocumentContext';
import { getIndexedDBData, putIndexedDBData } from '../../../../utils/indexedDBUtil';
import { AppStoreContext } from '../../../../context/appStoreContext';

const OVERRIDE_KEYS = ['colorMode', 'paperWeight', 'mailType', 'envelopeType', 'returnEnvelope'];
const CATALOG_OVERRIDE_KEYS = ['colorMode', 'mailType', 'envelopeType'];

function parseSavedFlat(raw) {
  if (raw == null) {
    return null;
  }
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }
  return raw;
}

function normalizeFlat(flat) {
  const base = { ...EMPTY_FLAT_TEMPLATE_OVERRIDES };
  const parsed = parseSavedFlat(flat);
  if (!parsed || typeof parsed !== 'object') {
    return base;
  }
  OVERRIDE_KEYS.forEach(key => {
    if (Object.prototype.hasOwnProperty.call(parsed, key)) {
      base[key] = parsed[key];
    }
  });
  return base;
}

function serializeFlat(flat) {
  return JSON.stringify(normalizeFlat(flat));
}

async function persistPrintOverridesToIndexedDB(printSettingsOverrides) {
  try {
    const current = await getIndexedDBData('complexDocument');
    if (!current || typeof current !== 'object') {
      return;
    }
    await putIndexedDBData('complexDocument', {
      ...current,
      printSettingsOverrides,
    });
  } catch (e) {
    console.warn('persistPrintOverridesToIndexedDB failed', e);
  }
}

function buildPrintOverridesPayload(draft) {
  const payload = normalizeFlat(draft);
  CATALOG_OVERRIDE_KEYS.forEach(k => {
    if (payload[k] === '') {
      payload[k] = null;
    }
  });
  return payload;
}

export const useTemplatePrintDefaults = templateId => {
  const printOverridesSelector = useCallback(
    store => store[COMPLEX_DOCUMENT_CONTEXT_KEYS.PRINT_SETTINGS_OVERRIDES],
    [],
  );
  const [savedFlatFromStore, store] = useDocumentContext(printOverridesSelector, 'useTemplatePrintDefaults');
  const { userDetails } = useContext(AppStoreContext);
  const organizationId = getOrganizationIdForPrintCatalog(userDetails);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [orgDefaults, setOrgDefaults] = useState(null);
  const [catalogMap, setCatalogMap] = useState(null);
  const [draft, setDraft] = useState(() => normalizeFlat(null));

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [defaultsRes, catalogData] = await Promise.all([
          getOrgPrintDefaults(),
          organizationId ? fetchOrgPrintCatalogMap(organizationId) : Promise.resolve(null),
        ]);
        if (cancelled) {
          return;
        }
        if (defaultsRes?.statusCode === PRINT_DEFAULTS_STATUS.GET_SUCCESS && defaultsRes.data) {
          setOrgDefaults(defaultsRes.data);
        } else {
          setOrgDefaults(null);
        }
        setCatalogMap(catalogData || null);
      } catch {
        if (!cancelled) {
          setOrgDefaults(null);
          setCatalogMap(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [organizationId]);

  useEffect(() => {
    setDraft(normalizeFlat(savedFlatFromStore));
  }, [savedFlatFromStore]);

  const dirty = useMemo(
    () => serializeFlat(draft) !== serializeFlat(savedFlatFromStore),
    [draft, savedFlatFromStore],
  );

  const persistSuccessfulResponse = useCallback(
    async responseData => {
      const normalized = normalizeFlat(responseData);
      store.set({
        [COMPLEX_DOCUMENT_CONTEXT_KEYS.PRINT_SETTINGS_OVERRIDES]: normalized,
      });
      await persistPrintOverridesToIndexedDB(normalized);
    },
    [store],
  );

  const save = useCallback(async () => {
    if (!templateId) {
      return { success: false, message: 'template-id-missing' };
    }
    setSaving(true);
    try {
      const payload = buildPrintOverridesPayload(draft);
      const response = await updateTemplatePrintOverrides(templateId, payload);
      if (response?.statusCode === PRINT_DEFAULTS_STATUS.TEMPLATE_UPDATE_SUCCESS) {
        await persistSuccessfulResponse(response.data);
        return { success: true };
      }
      return { success: false };
    } catch (error) {
      return { success: false };
    } finally {
      setSaving(false);
    }
  }, [templateId, draft, persistSuccessfulResponse]);

  const reset = useCallback(async () => {
    if (!templateId) {
      return { success: false, message: 'template-id-missing' };
    }
    setSaving(true);
    try {
      const response = await resetTemplatePrintOverrides(templateId);
      if (response?.statusCode === PRINT_DEFAULTS_STATUS.TEMPLATE_UPDATE_SUCCESS) {
        await persistSuccessfulResponse(response.data);
        return { success: true };
      }
      return { success: false };
    } catch (error) {
      return { success: false };
    } finally {
      setSaving(false);
    }
  }, [templateId, persistSuccessfulResponse]);

  return {
    orgDefaults,
    catalogMap,
    organizationId,
    draft,
    setDraft,
    save,
    reset,
    loading,
    saving,
    dirty,
  };
};
